import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  BookOpen, ArrowRight, ArrowLeft, Sparkles, Search,
  CheckCircle, Zap, Clock, Trophy, Brain
} from "lucide-react";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

// ─── Data ──────────────────────────────────────────────────────────────────────
const AVATARS = [
  "/e007f099a7f5f305a19330ae4be1e77c82513a49.png",
  "/c354e0bf285e0cf8127b18e0af0bae70e2ede61c.png",
  "/23ac5d1859811230e617c421baf74a03a4610d58.png",
  "/382dcbf5fd9c79f1c82798755b6cff2a616f0315.png",
  "/c9bd301403d07cb10efb98428bf26042e83a44f0.png",
  "/f05f701c96819518de792e08145675231d04a403.png",
  "/8e84ceeacd50a5f8b9bed7be92e203a8e91d1b6c.png",
  "/73f3053749530c7e3885b6f879b77b697c0f1b00.png",
];

const NIGERIAN_EXAMS = [
  { id: "JAMB", label: "JAMB", sub: "Joint Admissions & Matriculation Board", flag: "🇳🇬", color: "#16a34a", subjects: ["English Language", "Mathematics", "Physics", "Chemistry", "Biology", "Economics", "Government", "Geography", "Literature in English", "Commerce", "Accounting", "Agricultural Science", "Technical Drawing", "Yoruba", "Igbo", "Hausa", "French", "CRS", "IRS"] },
  { id: "WAEC", label: "WAEC", sub: "West African Examinations Council", flag: "🌍", color: "#2563eb", subjects: ["English Language", "Mathematics", "Physics", "Chemistry", "Biology", "Economics", "Government", "History", "Geography", "Literature", "Commerce", "Accounting", "Agricultural Science", "Further Mathematics", "Technical Drawing", "French", "CRS", "IRS"] },
  { id: "NECO", label: "NECO", sub: "National Examinations Council", flag: "🇳🇬", color: "#7c3aed", subjects: ["English Language", "Mathematics", "Physics", "Chemistry", "Biology", "Economics", "Government", "Geography", "Literature", "Commerce", "Accounting", "Agricultural Science"] },
  { id: "GCE", label: "GCE", sub: "Cambridge General Certificate of Education", flag: "🇬🇧", color: "#b45309", subjects: ["English Language", "Mathematics", "Physics", "Chemistry", "Biology", "Economics", "History", "Geography", "Computer Science", "Business Studies"] },
];

const INTERNATIONAL_EXAMS = [
  { id: "IELTS", label: "IELTS", sub: "International English Language Testing System", flag: "🌐", color: "#0891b2", subjects: ["Academic Reading", "Academic Writing (Task 1 & 2)", "Listening", "Speaking", "Grammar & Vocabulary"] },
  { id: "TOEFL", label: "TOEFL", sub: "Test of English as a Foreign Language", flag: "🇺🇸", color: "#0369a1", subjects: ["Reading Comprehension", "Listening", "Speaking", "Integrated Writing", "Independent Writing"] },
  { id: "SAT", label: "SAT", sub: "Scholastic Assessment Test (College Board)", flag: "🇺🇸", color: "#dc2626", subjects: ["Math (No Calculator)", "Math (Calculator)", "Evidence-Based Reading", "Writing & Language", "Essay (Optional)"] },
  { id: "GMAT", label: "GMAT", sub: "Graduate Management Admission Test", flag: "🌐", color: "#7c3aed", subjects: ["Quantitative Reasoning", "Verbal Reasoning", "Integrated Reasoning", "Analytical Writing"] },
  { id: "GRE", label: "GRE", sub: "Graduate Record Examination", flag: "🌐", color: "#0f766e", subjects: ["Verbal Reasoning", "Quantitative Reasoning", "Analytical Writing", "Reading Comprehension"] },
  { id: "A-LEVEL", label: "A-Level", sub: "Cambridge Advanced Level", flag: "🇬🇧", color: "#1d4ed8", subjects: ["Mathematics", "Further Mathematics", "Physics", "Chemistry", "Biology", "Economics", "Business", "Computer Science", "Psychology", "Sociology"] },
  { id: "IB", label: "IB Diploma", sub: "International Baccalaureate", flag: "🌐", color: "#0369a1", subjects: ["Mathematics (HL/SL)", "Physics (HL/SL)", "Chemistry (HL/SL)", "Biology (HL/SL)", "Economics (HL/SL)", "History (HL/SL)", "English A", "TOK", "Extended Essay"] },
];

const JAMB_COMBOS = [
  { label: "Science (Medicine/Engineering)", subjects: ["English Language", "Mathematics", "Physics", "Chemistry"] },
  { label: "Science (Biological)", subjects: ["English Language", "Biology", "Chemistry", "Mathematics"] },
  { label: "Arts / Humanities", subjects: ["English Language", "Literature in English", "Government", "CRS"] },
  { label: "Social Sciences", subjects: ["English Language", "Mathematics", "Economics", "Government"] },
  { label: "Commercial", subjects: ["English Language", "Mathematics", "Economics", "Accounting"] },
  { label: "Agriculture", subjects: ["English Language", "Biology", "Agricultural Science", "Chemistry"] },
];

// ─── Mode Selection Screen ─────────────────────────────────────────────────────
const ModeSelect = ({ onSelect }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-6 w-full max-w-2xl mx-auto">
    <div className="text-center mb-2">
      <h2 className="text-2xl font-black text-gray-900 mb-2">What are you here to do?</h2>
      <p className="text-gray-400 text-sm">Choose your mode — we'll tailor everything for you.</p>
    </div>

    {/* Learn Mode */}
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect("learn")}
      className="w-full flex items-center gap-5 p-6 rounded-3xl border-2 border-gray-100 bg-white hover:border-blue-300 hover:shadow-xl transition-all text-left group"
    >
      <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
        <BookOpen size={26} className="text-blue-600" />
      </div>
      <div className="flex-1">
        <p className="font-black text-gray-900 text-lg leading-tight">Learn a Subject</p>
        <p className="text-sm text-gray-400 mt-0.5">Notes, quizzes, tests & AI-guided mastery for any topic you choose</p>
      </div>
      <ArrowRight size={20} className="text-gray-300 group-hover:text-blue-500 transition-colors shrink-0" />
    </motion.button>

    {/* Exam Mode */}
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect("exam")}
      className="w-full flex items-center gap-5 p-6 rounded-3xl border-2 border-transparent bg-gradient-to-br from-indigo-600 to-blue-700 hover:from-indigo-700 hover:to-blue-800 transition-all text-left group shadow-lg shadow-blue-200"
    >
      <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
        <Trophy size={26} className="text-white" />
      </div>
      <div className="flex-1">
        <p className="font-black text-white text-lg leading-tight">Exam Preparation</p>
        <p className="text-sm text-blue-200 mt-0.5">Structured, exam-style curriculum for JAMB, WAEC, IELTS, SAT & more</p>
      </div>
      <div className="shrink-0 bg-white/20 rounded-full px-3 py-1 text-xs font-black text-white">NEW</div>
    </motion.button>
  </motion.div>
);

// ─── Avatar Picker ──────────────────────────────────────────────────────────────
const AvatarPicker = ({ selected, onSelect }) => (
  <div>
    <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">Choose Your AI Tutor</p>
    <div className="grid grid-cols-4 gap-3">
      {AVATARS.map((src, i) => (
        <motion.button key={i} whileTap={{ scale: 0.95 }} onClick={() => onSelect(i)}
          className={`aspect-square rounded-2xl overflow-hidden border-3 transition-all ${selected === i ? "ring-4 ring-blue-500 ring-offset-2 scale-105" : "border-gray-100 hover:ring-2 hover:ring-blue-200"}`}>
          <img src={src} alt="Tutor" className="w-full h-full object-cover" />
        </motion.button>
      ))}
    </div>
  </div>
);

// ─── Learn Mode Flow ───────────────────────────────────────────────────────────
const LearnModeSetup = ({ onStart, onBack }) => {
  const [step, setStep] = useState(0); // 0: avatar, 1: course
  const [avatar, setAvatar] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [course, setCourse] = useState("");
  const [aiFeedback, setAiFeedback] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [history, setHistory] = useState([]);

  const student = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("student") || "null"); } catch { return null; }
  }, []);

  useEffect(() => {
    if (!student?.id) return;
    axios.get(`${BASE_URL}/api/ai/history/${student.id}`)
      .then(r => setHistory(r.data?.data || []))
      .catch(() => {});
  }, [student?.id]);

  useEffect(() => {
    if (searchTerm.length < 4) { setAiFeedback(null); return; }
    const t = setTimeout(async () => {
      setIsValidating(true);
      try {
        const res = await axios.post(`${BASE_URL}/api/ai/validate-course`, { courseName: searchTerm });
        if (res.data?.success && res.data.isValid) {
          setAiFeedback(res.data);
          setCourse(res.data.correctedName || searchTerm);
        } else {
          setAiFeedback({ isValid: false, reason: res.data?.reason || "Try a different search term." });
        }
      } catch { setAiFeedback(null); }
      finally { setIsValidating(false); }
    }, 900);
    return () => clearTimeout(t);
  }, [searchTerm]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl mx-auto">
      <AnimatePresence mode="wait">
        {/* Step 0: Avatar */}
        {step === 0 && (
          <motion.div key="avatar" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
            <AvatarPicker selected={avatar} onSelect={setAvatar} />
            <div className="flex justify-between mt-6">
              <button onClick={onBack} className="flex items-center gap-2 text-gray-400 font-bold hover:text-gray-600 transition-colors">
                <ArrowLeft size={16} /> Back
              </button>
              <button onClick={() => setStep(1)} className="flex items-center gap-2 bg-blue-600 text-white font-bold px-6 py-3 rounded-2xl hover:bg-blue-700 transition-all shadow-lg">
                Next <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 1: Course */}
        {step === 1 && (
          <motion.div key="course" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
            <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">What do you want to learn?</p>

            {/* History quick-picks */}
            {history.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest self-center">Recent:</span>
                {history.slice(0, 4).map(h => (
                  <button key={h.course_name} onClick={() => { setSearchTerm(h.course_name); setCourse(h.course_name); }}
                    className={`text-xs font-bold px-3 py-1.5 rounded-xl border transition-all ${course === h.course_name ? "bg-blue-600 text-white border-blue-600" : "bg-white border-gray-200 text-gray-600 hover:border-blue-300"}`}>
                    {h.course_name}
                  </button>
                ))}
              </div>
            )}

            {/* Search input */}
            <div className="relative mb-4">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><Search size={18} /></div>
              <input
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); if (!e.target.value) setCourse(""); }}
                placeholder="Type any subject: Mathematics, Python, Economics…"
                className="w-full pl-11 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-blue-400 outline-none font-medium text-gray-800 text-sm transition-all"
              />
              {isValidating && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Sparkles size={16} className="text-blue-400 animate-spin" />
                </div>
              )}
            </div>

            {/* AI validation feedback */}
            <AnimatePresence>
              {aiFeedback && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className={`mb-4 p-4 rounded-2xl flex items-center gap-3 ${aiFeedback.isValid ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}>
                  {aiFeedback.isValid ? <CheckCircle size={18} className="text-emerald-600 shrink-0" /> : <Zap size={18} className="text-amber-500 shrink-0" />}
                  <div>
                    <p className={`text-xs font-black ${aiFeedback.isValid ? "text-emerald-700" : "text-amber-700"}`}>
                      {aiFeedback.isValid ? `✓ "${aiFeedback.correctedName || searchTerm}" — ${aiFeedback.category}` : "Note"}
                    </p>
                    {!aiFeedback.isValid && <p className="text-xs text-amber-600 mt-0.5">{aiFeedback.reason}</p>}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex justify-between mt-4">
              <button onClick={() => setStep(0)} className="flex items-center gap-2 text-gray-400 font-bold hover:text-gray-600 transition-colors">
                <ArrowLeft size={16} /> Back
              </button>
              <button
                onClick={() => onStart({ avatar: AVATARS[avatar], course, focus: "General Learning", examContext: null })}
                disabled={!course}
                className="flex items-center gap-2 bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold px-8 py-3 rounded-2xl hover:bg-blue-700 transition-all shadow-lg"
              >
                Start Learning <Zap size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Exam Mode Flow ────────────────────────────────────────────────────────────
const ExamModeSetup = ({ onStart, onBack }) => {
  const [step, setStep] = useState(0); // 0: avatar, 1: exam type, 2: subjects (JAMB only: 4 subjects)
  const [avatar, setAvatar] = useState(0);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [customExam, setCustomExam] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [jambCombo, setJambCombo] = useState(null);


  const JAMB_LIKE = ["JAMB"];
  const MAX_SUBJECTS = JAMB_LIKE.includes(selectedExam?.id) ? 4 : 12;
  const MIN_SUBJECTS = 1;

  const toggleSubject = (sub) => {
    setSelectedSubjects(prev => {
      if (prev.includes(sub)) return prev.filter(s => s !== sub);
      if (prev.length >= MAX_SUBJECTS) return prev;
      return [...prev, sub];
    });
  };

  const handleJambCombo = (combo) => {
    setJambCombo(combo.label);
    setSelectedSubjects(combo.subjects);
  };

  const finalExamId = useCustom ? (customExam || "CUSTOM") : selectedExam?.id;
  const finalCourse = selectedSubjects.length > 0
    ? `${finalExamId}: ${selectedSubjects.join(" | ")}`
    : finalExamId;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl mx-auto">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {["Tutor", "Exam Type", "Subjects"].map((label, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center gap-1.5 text-xs font-bold transition-all ${step === i ? "text-indigo-600" : step > i ? "text-emerald-600" : "text-gray-300"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black transition-all ${step === i ? "bg-indigo-600 text-white" : step > i ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                {step > i ? "✓" : i + 1}
              </div>
              {label}
            </div>
            {i < 2 && <div className={`flex-1 h-0.5 rounded-full transition-all ${step > i ? "bg-emerald-300" : "bg-gray-100"}`} />}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* Step 0: Avatar */}
        {step === 0 && (
          <motion.div key="exam-avatar" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
            <AvatarPicker selected={avatar} onSelect={setAvatar} />
            <div className="flex justify-between mt-6">
              <button onClick={onBack} className="flex items-center gap-2 text-gray-400 font-bold hover:text-gray-600 transition-colors"><ArrowLeft size={16} /> Back</button>
              <button onClick={() => setStep(1)} className="flex items-center gap-2 bg-indigo-600 text-white font-bold px-6 py-3 rounded-2xl hover:bg-indigo-700 transition-all shadow-lg">Next <ArrowRight size={16} /></button>
            </div>
          </motion.div>
        )}

        {/* Step 1: Exam Type */}
        {step === 1 && (
          <motion.div key="exam-type" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
            <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">What exam are you preparing for?</p>

            {/* Nigerian Section */}
            <div className="mb-4">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">🇳🇬 Nigerian Exams</p>
              <div className="grid grid-cols-2 gap-3">
                {NIGERIAN_EXAMS.map(exam => (
                  <motion.button key={exam.id} whileTap={{ scale: 0.97 }}
                    onClick={() => { setSelectedExam(exam); setSelectedSubjects([]); setUseCustom(false); }}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${selectedExam?.id === exam.id && !useCustom ? "border-indigo-500 bg-indigo-50" : "border-gray-100 bg-white hover:border-indigo-200"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{exam.flag}</span>
                      <span className="font-black text-gray-900">{exam.label}</span>
                      {selectedExam?.id === exam.id && !useCustom && <CheckCircle size={14} className="text-indigo-600 ml-auto" />}
                    </div>
                    <p className="text-[10px] text-gray-400 leading-tight">{exam.sub}</p>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* International Section */}
            <div className="mb-4">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">🌐 International Exams</p>
              <div className="grid grid-cols-2 gap-3">
                {INTERNATIONAL_EXAMS.map(exam => (
                  <motion.button key={exam.id} whileTap={{ scale: 0.97 }}
                    onClick={() => { setSelectedExam(exam); setSelectedSubjects([]); setUseCustom(false); }}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${selectedExam?.id === exam.id && !useCustom ? "border-indigo-500 bg-indigo-50" : "border-gray-100 bg-white hover:border-indigo-200"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{exam.flag}</span>
                      <span className="font-black text-gray-900">{exam.label}</span>
                      {selectedExam?.id === exam.id && !useCustom && <CheckCircle size={14} className="text-indigo-600 ml-auto" />}
                    </div>
                    <p className="text-[10px] text-gray-400 leading-tight">{exam.sub}</p>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Custom Exam */}
            <div className={`mb-4 p-4 rounded-2xl border-2 transition-all ${useCustom ? "border-indigo-400 bg-indigo-50" : "border-gray-100 bg-gray-50"}`}>
              <label className="flex items-center gap-3 cursor-pointer mb-2">
                <input type="checkbox" checked={useCustom} onChange={e => { setUseCustom(e.target.checked); if (e.target.checked) setSelectedExam(null); }}
                  className="w-4 h-4 text-indigo-600" />
                <span className="font-bold text-sm text-gray-700">Other / Custom Exam</span>
              </label>
              {useCustom && (
                <input value={customExam} onChange={e => setCustomExam(e.target.value)}
                  placeholder="E.g. LSAT, MCAT, Nigerian Bar Exam…"
                  className="w-full rounded-xl border border-indigo-200 px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-400 bg-white" />
              )}
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(0)} className="flex items-center gap-2 text-gray-400 font-bold hover:text-gray-600 transition-colors"><ArrowLeft size={16} /> Back</button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedExam && !useCustom}
                className="flex items-center gap-2 bg-indigo-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold px-6 py-3 rounded-2xl hover:bg-indigo-700 transition-all shadow-lg"
              >
                Select Subjects <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Subjects */}
        {step === 2 && (
          <motion.div key="subjects" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-gray-400">
                  {useCustom ? customExam : selectedExam?.label} — Select {JAMB_LIKE.includes(selectedExam?.id) ? '1–4' : '1–12'} subjects
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedSubjects.length}/{MAX_SUBJECTS} selected
                  {selectedExam?.id === "JAMB" ? " • You can focus on just 1 subject or pick up to 4" : " • You can pick 1 subject or up to 12"}
                </p>
              </div>
              <div className="text-sm font-black text-indigo-600">{Math.round((selectedSubjects.length / MAX_SUBJECTS) * 100)}%</div>
            </div>

            {/* JAMB quick combos */}
            {selectedExam?.id === "JAMB" && (
              <div className="mb-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Quick Combinations</p>
                <div className="flex flex-wrap gap-2">
                  {JAMB_COMBOS.map(c => (
                    <button key={c.label} onClick={() => handleJambCombo(c)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-xl border transition-all ${jambCombo === c.label ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-gray-200 text-gray-600 hover:border-indigo-300"}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected chips */}
            {selectedSubjects.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 p-3 bg-indigo-50 rounded-2xl border border-indigo-100">
                {selectedSubjects.map(s => (
                  <span key={s} onClick={() => toggleSubject(s)} className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl bg-indigo-600 text-white cursor-pointer hover:bg-indigo-700">
                    {s} <span className="text-indigo-200">×</span>
                  </span>
                ))}
              </div>
            )}

            {/* All subjects */}
            <div className="max-h-52 overflow-y-auto pr-1 flex flex-wrap gap-2 mb-5 custom-scrollbar">
              {(useCustom ? [] : selectedExam?.subjects || []).map(sub => {
                const chosen = selectedSubjects.includes(sub);
                const full = selectedSubjects.length >= MAX_SUBJECTS && !chosen;
                return (
                  <button key={sub} disabled={full} onClick={() => toggleSubject(sub)}
                    className={`text-xs font-bold px-3 py-2 rounded-xl border transition-all ${chosen ? "bg-indigo-600 text-white border-indigo-600" : full ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed" : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300"}`}>
                    {sub}
                  </button>
                );
              })}
              {useCustom && (
                <p className="text-xs text-gray-400 italic p-2">Curriculum will be auto-generated based on your exam type.</p>
              )}
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="flex items-center gap-2 text-gray-400 font-bold hover:text-gray-600 transition-colors"><ArrowLeft size={16} /> Back</button>
              <button
                onClick={() => onStart({ avatar: AVATARS[avatar], course: finalCourse, focus: "Exam Preparation", examContext: finalExamId })}
                disabled={!useCustom && selectedSubjects.length === 0}
                className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 disabled:from-gray-200 disabled:to-gray-200 disabled:text-gray-400 text-white font-bold px-8 py-3 rounded-2xl hover:from-indigo-700 hover:to-blue-700 transition-all shadow-lg"
              >
                <Trophy size={16} /> Start Exam Prep
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function PeerMateHomepage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("select"); // "select" | "learn" | "exam"

  const student = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("student") || "null"); } catch { return null; }
  }, []);

  const handleStart = ({ avatar, course, focus, examContext }) => {
    navigate("/peerchat", { state: { avatar, course, focus, examContext } });
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #fafaff 50%, #f5f0ff 100%)" }}
    >
      {/* Hero Header */}
      <div className="pt-12 pb-8 px-6 text-center">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-blue-600 flex items-center justify-center">
            <Brain size={22} className="text-white" />
          </div>
          <span className="font-black text-2xl text-gray-900">PeerMate <span className="text-blue-600">AI</span></span>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="text-3xl md:text-4xl font-black text-gray-900 mb-3 leading-tight">
          Your Personal<br />
          <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">AI Learning Partner</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-gray-400 max-w-sm mx-auto text-sm">
          Adaptive lessons, interactive quizzes, notes & AI scoring — tailored to exactly how you learn best.
        </motion.p>

        {/* Feature pills */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-2 mt-5">
          {[
            { icon: <BookOpen size={12} />, label: "Smart Notes" },
            { icon: <Zap size={12} />, label: "Duolingo-style Quizzes" },
            { icon: <Trophy size={12} />, label: "AI Scoring" },
            { icon: <Clock size={12} />, label: "Exam Ready in Days" },
          ].map(f => (
            <span key={f.label} className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 shadow-sm">
              <span className="text-blue-500">{f.icon}</span>{f.label}
            </span>
          ))}
        </motion.div>

        {/* Student greeting */}
        {student?.name && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="mt-4 text-sm font-bold text-blue-600">
            Welcome back, {student.name.split(" ")[0]} 👋
          </motion.p>
        )}
      </div>

      {/* Main Card */}
      <div className="flex-1 flex items-start justify-center px-4 pb-16">
        <div className="w-full max-w-2xl bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
          <AnimatePresence mode="wait">
            {mode === "select" && (
              <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <ModeSelect onSelect={setMode} />
              </motion.div>
            )}
            {mode === "learn" && (
              <motion.div key="learn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="flex items-center gap-2 mb-5">
                  <div className="p-2 bg-blue-50 rounded-xl"><BookOpen size={18} className="text-blue-600" /></div>
                  <div>
                    <p className="font-black text-gray-900 text-sm">Learn a Subject</p>
                    <p className="text-xs text-gray-400">Notes, quizzes & AI mastery</p>
                  </div>
                </div>
                <LearnModeSetup onStart={handleStart} onBack={() => setMode("select")} />
              </motion.div>
            )}
            {mode === "exam" && (
              <motion.div key="exam" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="flex items-center gap-2 mb-5">
                  <div className="p-2 bg-indigo-100 rounded-xl"><Trophy size={18} className="text-indigo-600" /></div>
                  <div>
                    <p className="font-black text-gray-900 text-sm">Exam Preparation</p>
                    <p className="text-xs text-gray-400">Structured, exam-style curriculum</p>
                  </div>
                </div>
                <ExamModeSetup onStart={handleStart} onBack={() => setMode("select")} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
